import { ApiService } from "../../../config";

export type SeoUsage = "servico" | "produto" | "post" | "qa" | "review";

export type SeoResearchItem = {
  keyword: string;
  nome: string;
  descricao: string;
  uso: SeoUsage;
  intencao: "alta" | "media" | "baixa";
  prioridade: number;
  confirmar: boolean;
  motivoConfirmar: string;
};

export type SeoResearch = {
  diagnostico: string;
  grupos: Array<{ grupo: string; keywords: string[] }>;
  itens: SeoResearchItem[];
  produtos: Array<{ nome: string; cobre: string[]; descricao: string }>;
  georanking: { p1: string[]; p2: string[]; p3: string[] };
  usoEstrategico: {
    servicos: string[];
    produtos: string[];
    posts: string[];
    qa: string[];
    avaliacoes: string[];
  };
  confirmarAntes: string[];
  alertas: string[];
};

export type SeoCounts = {
  services: number;
  products: number;
  today: number;
  dailyCap: number;
};

export type SeoJob = {
  jobId: string;
  profileId: string;
  mode: "services" | "products" | "service_descriptions";
  status: "QUEUED" | "CLAIMED" | "RUNNING" | "COMPLETED" | "FAILED";
  dryRun: boolean;
  total: number;
  done: number;
  current: string;
  log: string[];
  error?: string;
};

export type BrowserStatusState =
  | "ONLINE_OK"
  | "ACCOUNT_CONFIRMATION_REQUIRED"
  | "ACCOUNT_MISSING"
  | "LOGIN_REQUIRED"
  | "WORKER_OFFLINE";

export type BrowserStatus = {
  workerOnline: boolean;
  expectedEmail: string | null;
  accountAvailable: boolean;
  detectedAccounts?: string[];
  state: BrowserStatusState;
};


export type StockImage = {
  id: number;
  thumbUrl: string;
  fullUrl: string;
  photographer: string | null;
  alt: string | null;
};

export type StockImageDataUrl = {
  dataUrl: string;
  contentType: string;
  name: string;
};

const data = <T>(response: { data?: { data?: T } }): T =>
  response.data?.data as T;

export const seoProfileApi = {
  async research(profileId: string, context = ""): Promise<SeoResearch> {
    return data<SeoResearch>(
      await ApiService.post("/seo/research", { profileId, context }),
    );
  },

  async getLatestResearch(profileId: string): Promise<SeoResearch | null> {
    return data<SeoResearch | null>(
      await ApiService.get(`/seo/research/latest/${profileId}`),
    );
  },

  async publish(payload: {
    profileId: string;
    mode: "services" | "products" | "service_descriptions";
    dryRun: boolean;
    items: Array<{
      name: string;
      description: string;
      requiresConfirmation: boolean;
      confirmed: boolean;
      imageName?: string;
      imageContentType?: string;
      imageDataUrl?: string;
    }>;
  }): Promise<SeoJob> {
    return data<SeoJob>(await ApiService.post("/seo/publish", payload));
  },

  async getStatus(jobId: string): Promise<SeoJob> {
    return data<SeoJob>(await ApiService.get(`/seo/status/${jobId}`));
  },

  async getCounts(profileId: string): Promise<SeoCounts> {
    return data<SeoCounts>(await ApiService.get(`/seo/counts/${profileId}`));
  },

  async getBrowserStatus(profileId: string): Promise<BrowserStatus> {
    return data<BrowserStatus>(
      await ApiService.get(`/seo/browser-status/${profileId}`),
    );
  },

  async requestBrowserLogin(profileId: string): Promise<BrowserStatus> {
    return data<BrowserStatus>(
      await ApiService.post(`/seo/browser-login/${profileId}`),
    );
  },

  async confirmBrowserAccount(
    profileId: string,
    email: string,
  ): Promise<BrowserStatus> {
    return data<BrowserStatus>(
      await ApiService.post(`/seo/browser-account/${profileId}/confirm`, {
        email,
      }),
    );
  },

  async searchStockImages(query: string, n = 9, page = 1): Promise<StockImage[]> {
    return data<StockImage[]>(
      await ApiService.get(`/seo/stock-images`, { params: { q: query, n, page } }),
    );
  },

  async stockImageToDataUrl(url: string): Promise<StockImageDataUrl> {
    return data<StockImageDataUrl>(
      await ApiService.get(`/seo/stock-images/data-url`, { params: { url } }),
    );
  },
};